import { useEffect, useRef } from 'react';
import styles from './AnimatedWave.module.css';

const vertexSource = `
  attribute vec2 position;
  varying vec2 uv;
  void main() {
    uv = position * 0.5 + 0.5;
    gl_Position = vec4(position, 0.0, 1.0);
  }
`;

const fragmentSource = `
  precision mediump float;
  varying vec2 uv;
  uniform sampler2D image;
  uniform float time;
  void main() {
    vec2 p = uv;
    float envelope = sin(p.x * 3.14159);
    p.y += sin(p.x * 8.0 + time * 0.44) * 0.035 * envelope;
    p.y += cos(p.x * 14.0 - time * 0.27 + p.y * 3.0) * 0.015 * envelope;
    p.x += sin(p.y * 7.0 + time * 0.2) * 0.012 * envelope;
    p = clamp(p, vec2(0.002), vec2(0.998));
    vec4 color = texture2D(image, p);
    float light = sin(p.x * 10.0 - time * 0.38 + p.y * 4.0) * 0.025;
    color.rgb += light * (1.0 - color.rgb);
    gl_FragColor = color;
  }
`;

type Props = { src: string; paused: boolean };

/** GPU-деформация текстуры; без WebGL остаётся обычное изображение. */
export function AnimatedWave({ src, paused }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const controlsRef = useRef<{ pause: (value: boolean) => void } | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const gl = canvas.getContext('webgl', {
      alpha: false,
      antialias: false,
      depth: false,
      powerPreference: 'low-power',
    });
    if (!gl) return;

    const shaders: WebGLShader[] = [];
    const compile = (type: number, source: string) => {
      const shader = gl.createShader(type);
      if (!shader) return null;
      shaders.push(shader);
      gl.shaderSource(shader, source);
      gl.compileShader(shader);
      return gl.getShaderParameter(shader, gl.COMPILE_STATUS) ? shader : null;
    };
    const vertex = compile(gl.VERTEX_SHADER, vertexSource);
    const fragment = compile(gl.FRAGMENT_SHADER, fragmentSource);
    const program = gl.createProgram();
    if (!vertex || !fragment || !program) {
      shaders.forEach((shader) => gl.deleteShader(shader));
      if (program) gl.deleteProgram(program);
      return;
    }
    gl.attachShader(program, vertex);
    gl.attachShader(program, fragment);
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      shaders.forEach((shader) => gl.deleteShader(shader));
      gl.deleteProgram(program);
      return;
    }
    gl.useProgram(program);
    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]),
      gl.STATIC_DRAW
    );
    const position = gl.getAttribLocation(program, 'position');
    gl.enableVertexAttribArray(position);
    gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);

    const texture = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.uniform1i(gl.getUniformLocation(program, 'image'), 0);
    const timeUniform = gl.getUniformLocation(program, 'time');
    let frame = 0;
    let elapsed = 0;
    let lastTime = 0;
    let isPaused = false;
    let isVisible = true;
    let loaded = false;
    let disposed = false;
    let contextLost = false;

    const draw = () => {
      if (!loaded || disposed || contextLost) return;
      gl.uniform1f(timeUniform, elapsed);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    };
    const tick = (now: number) => {
      frame = 0;
      if (disposed || contextLost || isPaused || !isVisible || document.hidden) return;
      // Ограничиваем отрисовку до 30 кадров/с; после паузы время не прыгает.
      if (!lastTime || now - lastTime >= 1000 / 30) {
        elapsed += lastTime ? Math.min(now - lastTime, 80) / 1000 : 0;
        lastTime = now;
        draw();
      }
      frame = requestAnimationFrame(tick);
    };
    const sync = () => {
      cancelAnimationFrame(frame);
      frame = 0;
      lastTime = 0;
      if (
        loaded &&
        !disposed &&
        !contextLost &&
        !isPaused &&
        isVisible &&
        !document.hidden
      ) {
        frame = requestAnimationFrame(tick);
      }
    };
    controlsRef.current = {
      pause: (value) => {
        isPaused = value;
        sync();
      },
    };
    const resize = () => {
      const bounds = canvas.getBoundingClientRect();
      const scale = Math.min(window.devicePixelRatio || 1, 1.5);
      canvas.width = Math.min(1920, Math.max(1, Math.round(bounds.width * scale)));
      canvas.height = Math.max(1, Math.round(bounds.height * scale));
      gl.viewport(0, 0, canvas.width, canvas.height);
      draw();
    };
    const image = new Image();
    image.onload = () => {
      if (disposed || contextLost) return;
      gl.bindTexture(gl.TEXTURE_2D, texture);
      gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGB, gl.RGB, gl.UNSIGNED_BYTE, image);
      loaded = true;
      resize();
      canvas.dataset.renderer = 'webgl';
      canvas.style.opacity = '1';
      sync();
    };
    image.src = src;
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);
    const io = new IntersectionObserver(([entry]) => {
      isVisible = entry.isIntersecting;
      sync();
    });
    io.observe(canvas);
    const onContextLost = () => {
      contextLost = true;
      canvas.style.opacity = '0';
      canvas.dataset.renderer = 'fallback';
      sync();
    };
    canvas.addEventListener('webglcontextlost', onContextLost);
    document.addEventListener('visibilitychange', sync);
    return () => {
      disposed = true;
      image.onload = null;
      cancelAnimationFrame(frame);
      ro.disconnect();
      io.disconnect();
      document.removeEventListener('visibilitychange', sync);
      canvas.removeEventListener('webglcontextlost', onContextLost);
      controlsRef.current = null;
      canvas.style.opacity = '0';
      gl.deleteTexture(texture);
      gl.deleteBuffer(buffer);
      gl.deleteProgram(program);
      shaders.forEach((shader) => gl.deleteShader(shader));
    };
  }, [src]);

  useEffect(() => {
    controlsRef.current?.pause(paused);
  }, [paused]);

  return (
    <div className={styles.wave} aria-hidden="true">
      <img src={src} alt="" width="1536" height="1024" fetchPriority="high" />
      <canvas ref={canvasRef} data-renderer="fallback" />
    </div>
  );
}
